import type { JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { usePostHog } from '@posthog/react';
import { Badge, Button, Card, ChevronLeftIcon } from '@viniciomendesr/rayrovisk-design-system';
import { useDeletePin, usePin } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { CopyToClipboard } from '../components/CopyToClipboard';
import { PromptBody } from '../components/PromptBody';
import { PromptEditor } from '../components/PromptEditor';
import type { Pin, PinKind, Staleness } from '../api/types';

/** Same words as the list card's badge (Screen 09) — one origin, one name. */
const ORIGIN_LABEL: Record<PinKind, string> = {
  library: 'Biblioteca',
  assembled: 'Prompt montado',
  script: 'Roteiro'
};

/**
 * Screen 10. Everything here comes from the payload, never from a decision made
 * on this screen: `staleness.kind` picks the notice, `pin.origin` picks the
 * back link. A second client (the app) reads the same fields and would draw
 * something different without either one needing to agree with this file.
 */
export function PinScreen(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const query = usePin(id);
  const remove = useDeletePin();

  const state = accessStateOf(query.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  if (query.isLoading) {
    return (
      <p style={{ font: '400 13px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-text-tertiary)' }}>
        Abrindo o fixado…
      </p>
    );
  }

  if (!query.data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }}>
        <h1 style={{ margin: 0, font: '400 26px/1.15 var(--rvk-font-expressive)' }}>
          Este fixado não existe mais.
        </h1>
        <Link to="/gerador/fixados">
          <Button variant="secondary" size="sm">
            Voltar para os fixados
          </Button>
        </Link>
      </div>
    );
  }

  const { pin, staleness } = query.data;
  const back = backTo(pin);

  async function handleRemove(): Promise<void> {
    try {
      await remove.mutateAsync(pin.id);
      posthog?.capture('pin_deleted', { kind: pin.kind });
      navigate('/gerador/fixados');
    } catch {
      // Stays on the screen with the mutation's own error state below.
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 860 }}>
      <Link to="/gerador/fixados">
        <Button variant="ghost" size="sm" iconStart={<ChevronLeftIcon size={14} />}>
          Fixados
        </Button>
      </Link>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Badge tone="accent" variant="subtle" size="sm">
          {ORIGIN_LABEL[pin.kind]}
        </Badge>
        <h1 style={{ margin: 0, font: '400 30px/1.15 var(--rvk-font-expressive)', textWrap: 'pretty' }}>
          {pin.label}
        </h1>
      </div>

      <StalenessNotice pin={pin} staleness={staleness} />

      <Card layer="functional" padding="lg">
        <PromptEditor
          identity={`pin:${pin.id}`}
          template={pin.body}
          surface="pin"
          sourceRef={pin.kind}
        />
      </Card>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Link to={back.to}>
          <Button variant="secondary" size="sm">
            {back.label}
          </Button>
        </Link>
        <Button variant="ghost" size="sm" loading={remove.isPending} onClick={() => void handleRemove()}>
          Remover
        </Button>
      </div>
      {remove.isError && (
        <p style={{ margin: 0, font: '400 13px/1.5 var(--rvk-font-functional)' }}>
          {(remove.error as Error).message}
        </p>
      )}
    </section>
  );
}

/**
 * The server refuses to decide a client's routing (`origin` carries no path),
 * so the three destinations are made here — the one place the web owns them.
 */
function backTo(pin: Pin): { to: string; label: string } {
  if (pin.origin.kind === 'library') {
    return { to: `/biblioteca/${pin.origin.ref}`, label: 'Ver na biblioteca' };
  }
  const base = pin.origin.kind === 'script' ? '/roteiros' : '/gerador/montar';
  return { to: `${base}?tarefa=${encodeURIComponent(pin.origin.task ?? '')}`, label: 'Gerar outra versão' };
}

/**
 * One notice per `staleness.kind`, decided server-side. No branch here reads a
 * date — that decision already happened, and a second client has no way to
 * repeat it identically.
 */
function StalenessNotice({ pin, staleness }: { pin: Pin; staleness: Staleness }): JSX.Element | null {
  if (staleness.kind === 'none') {
    return null;
  }

  if (staleness.kind === 'profile_changed') {
    return (
      <div style={NOTICE_STYLE}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ font: '500 14.5px/1.4 var(--rvk-font-functional)' }}>
            Seu perfil mudou desde que você fixou isso.
          </span>
          <span style={{ font: '400 12.5px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-text-secondary)' }}>
            Esta é a versão que sairia agora, com o que está no seu perfil hoje.
          </span>
        </div>
        <PromptBody text={staleness.prompt} />
        <div>
          <CopyToClipboard
            text={staleness.prompt}
            surface="pin-refreshed"
            sourceRef={pin.kind}
            label="Copiar a versão de agora"
            variant="secondary"
            size="sm"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...NOTICE_STYLE, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ font: '500 14.5px/1.4 var(--rvk-font-functional)' }}>
          Você gerou outro roteiro a partir dessa mesma ideia.
        </span>
        <span style={{ font: '400 12.5px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-text-secondary)' }}>
          Gere de novo a partir da mesma ideia.
        </span>
      </div>
      <Link to={`/roteiros?tarefa=${encodeURIComponent(pin.origin.task ?? '')}`}>
        <Button variant="secondary" size="sm">
          Gerar roteiro
        </Button>
      </Link>
    </div>
  );
}

const NOTICE_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 14,
  flexWrap: 'wrap' as const,
  padding: '14px 16px',
  background: 'var(--rvk-surface-1)',
  border: '1px solid var(--rvk-border-default)',
  borderLeft: '3px solid var(--rvk-accent-default)',
  borderRadius: 'var(--rvk-radius-md)'
};
